import { useEffect } from "react";
import { useSEO } from "@/hooks/use-seo";
import { motion } from "framer-motion";
import { Search, PenTool, Zap, RefreshCcw } from "lucide-react";
import { useLocation } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExpertContact from "@/components/ExpertContact";
import logoW from "@assets/W_1771840384312.webp";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const gradText = "bg-[linear-gradient(92deg,#4BF4B9,#86F0B4)] bg-clip-text text-transparent";

export default function Werkwijze() {
  const [, setLocation] = useLocation();
  const { language, localePath } = useLanguage();
  const nl = language === 'nl';

  useSEO({
    title: nl
      ? 'Werkwijze | Wrkt Digital - Data-Driven Growth Methode D2C Merken'
      : 'Our Method | Wrkt Digital - Data-Driven Growth Method for D2C Brands',
    description: nl
      ? 'Ontdek de Growth Compass methode van Wrkt Digital: 4 fasen van Intelligence, Creation, Execution tot Retention. AI-gestuurde campagnestrategie, data-driven creative productie en 24/7 geautomatiseerde optimalisatie voor D2C e-commerce merken.'
      : 'Discover the Growth Compass method by Wrkt Digital: 4 phases from Intelligence, Creation, Execution to Retention. AI-driven campaign strategy, data-driven creative production and 24/7 automated optimization for D2C e-commerce brands.',
    path: '/werkwijze',
    lang: nl ? 'nl' : 'en',
    keywords: nl
      ? 'werkwijze digital marketing, growth compass methode, data-driven marketing strategie, AI campagne optimalisatie, creative strategie e-commerce, performance marketing aanpak, 24/7 geautomatiseerde optimalisatie, cross-channel marketing strategie, D2C marketing methode, e-commerce groei framework'
      : 'digital marketing method, growth compass method, data-driven marketing strategy, AI campaign optimization, creative strategy e-commerce, performance marketing approach, 24/7 automated optimization, cross-channel marketing strategy, D2C marketing method, e-commerce growth framework',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const phases = [
    {
      num: '01',
      icon: Search,
      title: 'Intelligence',
      subtitle: nl ? 'We analyseren, jij begrijpt' : 'We analyze, you understand',
      desc: nl
        ? 'Voordat we één euro uitgeven, analyseren we alles. Je advertentieaccounts, ad creatives en hun performance, seizoenstrends en conversiedata. Maar ook de buitenkant: je concurrenten, hun ad libraries en markttrends. Cross-channel, zodat je een compleet beeld krijgt van waar de kansen liggen.'
        : 'Before we spend a single euro, we analyze everything. Your ad accounts, creatives and their performance, seasonal trends and conversion data. But also the outside: your competitors, their ad libraries and market trends. Cross-channel, so you get a full picture of where the opportunities are.',
      features: nl
        ? ['AI data scan', 'Concurrentie- en marktanalyse', 'Ad account en creative performance review', 'Seizoenstrend- en conversieanalyse', 'Cross-channel gap analyse']
        : ['AI data scan', 'Competitor and market analysis', 'Ad account and creative performance review', 'Seasonal trend and conversion analysis', 'Cross-channel gap analysis'],
    },
    {
      num: '02',
      icon: PenTool,
      title: 'Creation',
      subtitle: nl ? 'Creatives gebouwd op data' : 'Creatives built on data',
      desc: nl
        ? 'Onze creative strategie is gebouwd op data. We ontwikkelen en produceren creatives in-house met een content- en hookframework. Elke visual, elke hook en elk format is gebaseerd op wat we in de data zien werken.'
        : 'Our creative strategy is built on data. We develop and produce creatives in-house using a content and hook framework. Every visual, every hook and every format is based on what we see working in the data.',
      features: nl
        ? ['In-house creative productie', 'Content- en hookframework', 'Creatives gebaseerd op winnende formats', 'Snelle iteratie op basis van resultaten', 'Cross-channel creative optimalisatie']
        : ['In-house creative production', 'Content and hook framework', 'Creatives based on winning formats', 'Rapid iteration based on results', 'Cross-channel creative optimization'],
    },
    {
      num: '03',
      icon: Zap,
      title: 'Execution',
      subtitle: nl ? 'Dagelijks bijgestuurd' : 'Adjusted daily',
      desc: nl
        ? 'Zodra campagnes live zijn, kijkt onze AI-tooling 24/7 mee. We sturen dagelijks bij op rendement: budgetallocatie over kanalen heen, doorlopende tests en realtime alerts zodra iets afwijkt.'
        : 'Once campaigns go live, our AI tooling watches 24/7. We adjust daily, steering on return: cross-channel budget allocation, continuous testing and realtime alerts whenever something deviates.',
      features: nl
        ? ['Cross-channel campagnemanagement', 'Dagelijkse optimalisatie op rendement', 'Realtime performance-alerts', 'Doorlopende A/B-tests']
        : ['Cross-channel campaign management', 'Daily optimization on return', 'Realtime performance alerts', 'Continuous A/B testing'],
    },
    {
      num: '04',
      icon: RefreshCcw,
      title: 'Retention',
      subtitle: nl ? 'Klanten die blijven terugkomen' : 'Customers that keep coming back',
      desc: nl
        ? 'Nieuwe klanten winnen is duur — bestaande klanten laten terugkomen is winst. We bouwen e-mail- en CRM-flows die de klantwaarde over tijd verhogen, van winback tot loyaliteit, zodat elke euro adspend structureel harder werkt.'
        : 'Winning new customers is expensive — getting existing customers to return is profit. We build email and CRM flows that increase customer value over time, from winback to loyalty, so every euro of ad spend works structurally harder.',
      features: nl
        ? ['E-mail- en CRM-flows', 'Retargeting op bestaande klanten', 'LTV- en retentie-optimalisatie', 'Loyalty- en winback-campagnes']
        : ['Email and CRM flows', 'Retargeting existing customers', 'LTV and retention optimization', 'Loyalty and winback campaigns'],
    },
  ];

  const signalPoints = [
    {
      title: nl ? 'Je concurrentie in beeld' : 'Your competition in view',
      text: nl
        ? 'Met Signal scannen we razendsnel honderden ads van jouw merk én je concurrenten. Zo zien we welke angles, formats en copy-patronen werken en waar de top spenders in jouw categorie op inzetten.'
        : 'With Signal we rapidly scan hundreds of ads from your brand and your competitors. We see which angles, formats and copy patterns work and what the top spenders in your category are betting on.',
    },
    {
      title: nl ? 'Testkansen voor morgen' : 'Test opportunities for tomorrow',
      text: nl
        ? 'Elke analyse vertalen we naar concrete tests: bewezen kansen uit je eigen data en nieuwe angles uit je markt. Die zetten we direct om in creatives en campagnes.'
        : 'We translate every analysis into concrete tests: proven opportunities from your own data and new angles from your market. We turn those directly into creatives and campaigns.',
    },
    {
      title: nl ? 'Elke advertentie geanalyseerd' : 'Every ad analyzed',
      text: nl
        ? 'Per ad zien we bereik, looptijd en prestatie. Zo weten we precies welke creatives we moeten ontwikkelen en testen.'
        : 'For every ad we see reach, runtime and performance. So we know exactly which creatives to develop and test.',
    },
  ];

  const nodes = [
    { label: 'Shopify', left: '50%', top: '12%' },
    { label: 'Meta', left: '86.2%', top: '31.3%' },
    { label: 'Google Ads', left: '86.2%', top: '72.9%' },
    { label: 'TikTok Ads', left: '50%', top: '89.6%' },
    { label: 'Klaviyo', left: '13.8%', top: '72.9%' },
    { label: 'GA4 & data', left: '13.8%', top: '31.3%' },
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-[#4CEAAC] selection:text-[#0A1F14]" role="main">
      <style>{`
        @keyframes wkRadar { to { transform: rotate(360deg); } }
        @keyframes wkPulse { 0%,100% { opacity: .5; } 50% { opacity: 1; } }
        .wk-sweep { transform-origin: 200px 200px; animation: wkRadar 8s linear infinite; }
        .wk-activedot { animation: wkPulse 2.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .wk-sweep, .wk-activedot { animation: none; }
        }
      `}</style>

      <Navbar activePage="werkwijze" />

      {/* ─── Hero ─── */}
      <section id="top" className="relative overflow-hidden bg-grad-hero has-grain">
        <img src={logoW} alt="" aria-hidden="true" className="absolute -top-[8%] -right-[4%] w-[38%] max-w-none opacity-[0.05] pointer-events-none select-none" />
        <div className="relative max-w-[1240px] mx-auto px-6 md:px-10 pt-32 pb-24 md:pt-[118px] md:pb-[116px] grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
          <div>
            <motion.p initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-[#4BF4B9] text-xs font-semibold uppercase tracking-[0.2em]">
              {nl ? 'Onze werkwijze' : 'Our method'}
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.06 }} className="font-display font-extrabold text-[clamp(48px,6.4vw,88px)] leading-[0.96] tracking-[-0.04em] mt-5">
              {nl ? 'Het ' : 'The '}<span className={gradText}>Growth<br />Compass.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }} className="text-[19px] leading-relaxed text-white/[0.78] max-w-[46ch] mt-7">
              {nl
                ? 'Vier fasen, één helder proces: van analyse tot retentie. Het Growth Compass vertaalt 15 jaar advertising-ervaring naar een werkwijze waarmee je elke maand weet waar je staat en wat de volgende stap is.'
                : 'Four phases, one clear process: from analysis to retention. The Growth Compass translates 15 years of advertising experience into a method that tells you every month where you stand and what the next step is.'}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18 }} className="flex gap-3.5 mt-10 flex-wrap">
              <button onClick={() => setLocation(localePath('/scan'))} className="btn btn--gradient btn--lg" data-testid="button-hero-scan">
                {nl ? 'Gratis Growth Scan →' : 'Free Growth Scan →'}
              </button>
              <a href="#method" className="btn btn--lg btn--outline-inverse" data-testid="button-hero-phases">
                {nl ? 'Bekijk de fasen' : 'See the phases'}
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.24 }} className="flex gap-10 mt-14 flex-wrap">
              <div>
                <div className="font-display font-extrabold text-[34px] tracking-[-0.03em] text-white">{nl ? '15 jr' : '15 yrs'}</div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-white/55 mt-1.5">{nl ? 'Advertising-ervaring' : 'Advertising experience'}</div>
              </div>
              <div className="w-px bg-white/[0.14]" />
              <div>
                <div className={`font-display font-extrabold text-[34px] tracking-[-0.03em] ${gradText}`}>24/7</div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-white/55 mt-1.5">{nl ? 'AI-tooling stuurt bij' : 'AI tooling adjusts'}</div>
              </div>
              <div className="w-px bg-white/[0.14]" />
              <div>
                <div className="font-display font-extrabold text-[34px] tracking-[-0.03em] text-white">4</div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-white/55 mt-1.5">{nl ? 'Fasen, één cyclus' : 'Phases, one cycle'}</div>
              </div>
            </motion.div>
          </div>

          {/* Compass */}
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative w-full max-w-[460px] mx-auto aspect-square" aria-hidden="true">
            <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
              <defs>
                <radialGradient id="wkSweepGrad" cx="200" cy="200" r="170" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#4BF4B9" stopOpacity="0.34" />
                  <stop offset="60%" stopColor="#4BF4B9" stopOpacity="0.08" />
                  <stop offset="100%" stopColor="#4BF4B9" stopOpacity="0" />
                </radialGradient>
                <linearGradient id="wkNeedle" x1="200" y1="60" x2="200" y2="200" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#86F0B4" />
                  <stop offset="100%" stopColor="#4BF4B9" />
                </linearGradient>
              </defs>
              <circle cx="200" cy="200" r="170" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="1" />
              <circle cx="200" cy="200" r="120" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="1" />
              <circle cx="200" cy="200" r="70" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
              <line x1="200" y1="18" x2="200" y2="382" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              <line x1="18" y1="200" x2="382" y2="200" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              <g className="wk-sweep">
                <path d="M200,200 L165.7,38.6 A165,165 0 0 1 234.3,38.6 Z" fill="url(#wkSweepGrad)" />
              </g>
              <circle className="wk-activedot" cx="200" cy="50" r="6" fill="#4BF4B9" />
              <circle cx="200" cy="50" r="11" fill="none" stroke="rgba(75,244,185,0.4)" strokeWidth="1" />
              <circle cx="350" cy="200" r="4.5" fill="rgba(255,255,255,0.55)" />
              <circle cx="200" cy="350" r="4.5" fill="rgba(255,255,255,0.55)" />
              <circle cx="50" cy="200" r="4.5" fill="rgba(255,255,255,0.55)" />
              <polygon points="200,64 210,200 190,200" fill="url(#wkNeedle)" />
              <polygon points="200,320 210,200 190,200" fill="rgba(255,255,255,0.22)" />
              <circle cx="200" cy="200" r="12" fill="#06150C" stroke="#4BF4B9" strokeWidth="1.5" />
              <circle cx="200" cy="200" r="3.5" fill="#4BF4B9" />
            </svg>
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-center whitespace-nowrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#4BF4B9]">01</span>
              <span className="font-display font-bold text-[15px] text-white ml-1.5">Intelligence</span>
            </div>
            <div className="absolute top-1/2 -right-3.5 -translate-y-1/2 whitespace-nowrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/50">02</span>
              <span className="font-display font-bold text-[15px] text-white/90 ml-1.5">Creation</span>
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-center whitespace-nowrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/50">03</span>
              <span className="font-display font-bold text-[15px] text-white/90 ml-1.5">Execution</span>
            </div>
            <div className="absolute top-1/2 -left-4 -translate-y-1/2 whitespace-nowrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/50">04</span>
              <span className="font-display font-bold text-[15px] text-white/90 ml-1.5">Retention</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Method: 4 fasen ─── */}
      <section id="method" className="relative bg-[#04100A] py-28">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="text-center max-w-[720px] mx-auto mb-16">
            <p className="text-[#4BF4B9] text-xs font-semibold uppercase tracking-[0.2em]">
              {nl ? 'Onze werkwijze' : 'Our method'}
            </p>
            <h2 className="font-display font-extrabold text-[clamp(34px,4.6vw,60px)] tracking-[-0.035em] leading-[1.02] mt-4">
              {nl ? 'Van analyse tot ' : 'From analysis to '}<span className={gradText}>{nl ? 'resultaat.' : 'results.'}</span>
            </h2>
            <div className="w-24 h-[5px] rounded-full mx-auto mt-6" style={{ background: 'var(--grad-green)' }} />
          </motion.div>

          <div className="flex flex-col gap-[22px]">
            {phases.map((phase, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.6, delay: 0.08 * i }}>
                <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center bg-white/[0.025] border border-white/[0.08] rounded-3xl p-7 md:p-10">
                  <div>
                    <div className="flex items-center gap-4">
                      <span className="font-display font-extrabold text-[40px] tracking-[-0.03em] text-white/[0.22] leading-none">{phase.num}</span>
                      <span className="inline-flex items-center justify-center w-[46px] h-[46px] rounded-xl bg-[#4BF4B9]/10 border border-[#4BF4B9]/[0.22] text-[#4BF4B9]">
                        <phase.icon className="w-[22px] h-[22px]" strokeWidth={1.8} />
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-[30px] tracking-[-0.02em] text-white mt-5">{phase.title}</h3>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6FC79A] mt-2">{phase.subtitle}</div>
                    <p className="text-[15.5px] leading-[1.68] text-white/[0.66] mt-4 max-w-[52ch]">{phase.desc}</p>
                  </div>
                  <div className="bg-black/[0.35] border border-white/[0.07] rounded-2xl px-7 py-6">
                    <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-white/45">
                      {nl ? 'Wat je krijgt' : 'What you get'}
                    </div>
                    <ul className="mt-4 flex flex-col gap-3.5">
                      {phase.features.map((feat, j) => (
                        <li key={j} className="flex gap-3 items-start text-[14.5px] text-white/[0.86]">
                          <span className="text-[#4BF4B9] flex-none">✓</span>{feat}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Alles verbonden ─── */}
      <section className="relative bg-[#04100A] py-28 overflow-hidden">
        <div className="absolute top-[44%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[620px] h-[620px] bg-[radial-gradient(circle,rgba(75,244,185,0.10)_0%,rgba(75,244,185,0)_66%)] pointer-events-none" />
        <div className="relative max-w-[1240px] mx-auto px-6 md:px-10">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="text-center max-w-[760px] mx-auto">
            <h2 className="font-display font-extrabold text-[clamp(38px,5.2vw,66px)] tracking-[-0.04em] leading-none">
              {nl ? 'Alles verbonden.' : 'Everything connected.'}<br />
              <span className={gradText}>{nl ? 'Alles meetbaar.' : 'Everything measurable.'}</span>
            </h2>
            <p className="text-lg leading-relaxed text-white/70 max-w-[52ch] mx-auto mt-6">
              {nl
                ? 'Wrkt Signal koppelt je shop, kanalen en CRM, leest je markt en voedt elke beslissing met data. Jij ziet het resultaat in één dashboard.'
                : 'Wrkt Signal connects your shop, channels and CRM, reads your market and feeds every decision with data. You see the result in one dashboard.'}
            </p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} className="relative w-full max-w-[780px] h-[380px] md:h-[480px] mx-auto mt-14" aria-hidden="true">
            <svg viewBox="0 0 780 480" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
              <g stroke="rgba(75,244,185,0.3)" strokeWidth="1.5" strokeDasharray="2 7" strokeLinecap="round" fill="none">
                <line x1="390" y1="240" x2="390" y2="58" />
                <line x1="390" y1="240" x2="672" y2="150" />
                <line x1="390" y1="240" x2="672" y2="350" />
                <line x1="390" y1="240" x2="390" y2="430" />
                <line x1="390" y1="240" x2="108" y2="350" />
                <line x1="390" y1="240" x2="108" y2="150" />
              </g>
            </svg>
            <div className="has-grain absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140px] h-[140px] md:w-[168px] md:h-[168px] rounded-full flex flex-col items-center justify-center text-center border border-[#4BF4B9]/40"
              style={{ background: 'radial-gradient(farthest-corner at 60% 30%, #1E6B41, #06150C 82%)', boxShadow: '0 0 60px rgba(75,244,185,0.28), inset 0 1px 0 rgba(255,255,255,0.1)' }}>
              <div className="font-display font-extrabold text-[22px] tracking-[-0.02em] text-white">Wrkt Signal</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4BF4B9] mt-1">{nl ? 'AI-kern' : 'AI core'}</div>
            </div>
            {nodes.map((node, i) => (
              <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 px-4 py-2.5 md:px-[22px] md:py-3 rounded-[14px] bg-white/[0.04] border border-white/10 font-display font-bold text-[12px] md:text-[14.5px] text-white/90 whitespace-nowrap"
                style={{ left: node.left, top: node.top }}>
                {node.label}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── Wrkt Signal ─── */}
      <section className="relative bg-black py-28">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-[0.92fr_1.08fr] gap-16 items-center">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }}>
            <p className="text-[#4BF4B9] text-xs font-semibold uppercase tracking-[0.2em]">
              {nl ? 'Eigen AI-tooling' : 'Our own AI tooling'}
            </p>
            <h2 className="font-display font-extrabold text-[clamp(34px,4.4vw,54px)] tracking-[-0.035em] leading-[1.02] mt-4">
              {nl ? 'Blijf de concurrentie voor.' : 'Stay ahead of the competition.'}<br />
              <span className={gradText}>{nl ? 'Met Wrkt Signal.' : 'With Wrkt Signal.'}</span>
            </h2>
            <div className="flex flex-col gap-7 mt-10">
              {signalPoints.map((point, i) => (
                <div key={i} className="flex gap-[18px]">
                  <span className="flex-none w-[30px] h-[30px] rounded-full border border-[#4BF4B9]/35 bg-[#4BF4B9]/[0.08] text-[#4BF4B9] font-display font-bold text-[13px] flex items-center justify-center mt-0.5">{i + 1}</span>
                  <div>
                    <div className="font-display font-bold text-[19px] tracking-[-0.01em] text-white">{point.title}</div>
                    <p className="text-[15px] leading-[1.66] text-white/[0.64] mt-2 max-w-[48ch]">{point.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <button onClick={() => setLocation(localePath('/contact'))} className="btn btn--gradient btn--lg" data-testid="button-signal-demo">
                {nl ? 'Vraag een demo aan →' : 'Request a demo →'}
              </button>
            </div>
          </motion.div>

          {/* Browser mock */}
          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} aria-hidden="true">
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0A1712]" style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 60px rgba(20,70,41,0.25)' }}>
              <div className="flex items-center gap-2 px-4 py-[13px] bg-[#0B2116]/50 border-b border-white/[0.07]">
                <span className="w-[11px] h-[11px] rounded-full bg-white/[0.18]" />
                <span className="w-[11px] h-[11px] rounded-full bg-white/[0.18]" />
                <span className="w-[11px] h-[11px] rounded-full bg-white/[0.18]" />
                <span className="mx-auto px-4 py-[5px] rounded-full bg-white/[0.06] text-[11.5px] text-white/70 inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-[3px] inline-block" style={{ background: 'var(--grad-green)' }} />signal.wrkt.nl
                </span>
              </div>
              <div className="p-5 flex flex-col gap-3.5">
                <div className="flex items-center gap-3">
                  <div className="h-[7px] w-[92px] rounded-full" style={{ background: 'var(--grad-green)' }} />
                  <div className="h-[7px] flex-1 rounded-full bg-white/[0.07]" />
                  <div className="font-display font-extrabold text-[15px] text-[#4BF4B9]">83</div>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                    <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">{nl ? 'Creative kaartje' : 'Creative card'}</div>
                    <div className="flex flex-col gap-2 mt-3">
                      <div className="h-2 w-[88%] rounded-full bg-[#46A572]" />
                      <div className="h-2 w-[64%] rounded-full bg-[#2F8351]" />
                      <div className="h-2 w-[74%] rounded-full bg-[#4BF4B9]" />
                      <div className="h-2 w-[42%] rounded-full bg-white/[0.12]" />
                      <div className="h-2 w-[55%] rounded-full bg-[#2F8351]" />
                    </div>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                    <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">Angle + format</div>
                    <div className="h-1.5 w-[90%] rounded-full bg-white/10 mt-3" />
                    <div className="h-1.5 w-[70%] rounded-full bg-white/[0.08] mt-2" />
                    <div className="flex gap-1.5 mt-3.5">
                      <span className="px-3 py-[5px] rounded-[7px] bg-[#4BF4B9]/[0.12] border border-[#4BF4B9]/25 text-[10px] font-bold text-[#4BF4B9]">Video</span>
                      <span className="px-3 py-[5px] rounded-[7px] bg-white/[0.05] border border-white/10 text-[10px] font-bold text-white/60">Static</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                  <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">{nl ? 'Copy patronen' : 'Copy patterns'}</div>
                  <div className="grid grid-cols-4 gap-3 mt-3">
                    <div><div className="font-display font-extrabold text-lg text-white">13</div><div className="h-[5px] w-[80%] rounded-full bg-[#46A572] mt-1.5" /></div>
                    <div><div className="font-display font-extrabold text-lg text-white">61%</div><div className="h-[5px] w-[60%] rounded-full bg-[#2F8351] mt-1.5" /></div>
                    <div><div className="font-display font-extrabold text-lg text-white">38%</div><div className="h-[5px] w-[40%] rounded-full bg-white/[0.14] mt-1.5" /></div>
                    <div><div className="font-display font-extrabold text-lg text-[#4BF4B9]">5.8×</div><div className="h-[5px] w-[90%] rounded-full bg-[#4BF4B9] mt-1.5" /></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                    <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">Top spenders</div>
                    <div className="flex flex-col gap-2.5 mt-3">
                      <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-[5px] bg-gradient-to-br from-[#256C43] to-[#4BF4B9]" /><div className="h-1.5 flex-1 rounded-full bg-white/10" /></div>
                      <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-[5px] bg-gradient-to-br from-[#1B5234] to-[#38B083]" /><div className="h-1.5 flex-1 rounded-full bg-white/[0.08]" /></div>
                      <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-[5px] bg-gradient-to-br from-[#123420] to-[#2F8351]" /><div className="h-1.5 flex-1 rounded-full bg-white/[0.08]" /></div>
                    </div>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                    <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">{nl ? "Landingspagina's" : 'Landing pages'}</div>
                    <div className="flex flex-col gap-2 mt-3">
                      <div className="h-[7px] w-[85%] rounded-full bg-[#E0A43B]" />
                      <div className="h-[7px] w-[55%] rounded-full bg-[#E0A43B]/40" />
                      <div className="h-[7px] w-[68%] rounded-full bg-white/10" />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42] mb-2.5">{nl ? 'Top presterende creatives' : 'Top performing creatives'}</div>
                  <div className="grid grid-cols-3 gap-3">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="aspect-[3/4] rounded-[10px] border border-white/[0.07] flex items-end p-2.5"
                        style={{ background: `linear-gradient(${160 + i * 25}deg, rgba(75,244,185,${0.14 - i * 0.03}), rgba(6,21,12,0.9) 70%)` }}>
                        <div className="w-full">
                          <div className="h-1.5 w-3/4 rounded-full bg-white/20" />
                          <div className="h-1.5 w-1/2 rounded-full bg-[#4BF4B9]/50 mt-1.5" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section id="cta" className="relative bg-grad-duo has-grain py-[120px] px-6 md:px-10 overflow-hidden">
        <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="relative max-w-[900px] mx-auto text-center">
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.2em] text-white/70">
            {nl
              ? 'Dit is hoe we werken. De eerste stap? Een gratis Growth Scan.'
              : 'This is how we work. The first step? A free Growth Scan.'}
          </p>
          <h2 className="font-display font-extrabold text-[clamp(40px,6.2vw,80px)] tracking-[-0.04em] leading-[0.98] mt-5">
            {nl ? 'Klaar om het ' : 'Ready to do things '}
            <span className={gradText}>{nl ? 'anders' : 'differently'}</span>
            {nl ? ' te doen?' : '?'}
          </h2>
          <p className="text-[19px] leading-relaxed text-white/[0.78] max-w-[48ch] mx-auto mt-6">
            {nl
              ? 'Ontdek wat jouw campagnes beter kunnen. Start met een gratis Growth Scan. Vrijblijvend, met een concreet beeld van je grootste kansen.'
              : 'Find out where your campaigns can improve. Start with a free Growth Scan. No strings attached, with a concrete view of your biggest opportunities.'}
          </p>
          <div className="flex gap-3.5 mt-10 justify-center flex-wrap">
            <button onClick={() => setLocation(localePath('/scan'))} className="btn btn--gradient btn--lg" data-testid="button-cta-scan">
              {nl ? 'Start je gratis Growth Scan →' : 'Start your free Growth Scan →'}
            </button>
            <button onClick={() => setLocation(localePath('/contact'))} className="btn btn--lg btn--outline-inverse" data-testid="button-cta-intro">
              {nl ? 'Plan een kennismaking →' : 'Book an intro call →'}
            </button>
          </div>
        </motion.div>
      </section>

      <ExpertContact />

      <Footer />
    </div>
  );
}
